/**
 * Phase 19 / Task 19.4 (DEC-325) — data for the Finance dashboard.
 *
 * GET /v1/dashboards/finance. Types mirror packages/api/src/services/metrics/
 * finance-dashboard.ts (copied by hand, like every web hook). Every VIEW_INVOICES role
 * receives the money fields (DEC-332).
 */

import { useApiQuery, type useApiOpts } from '@lodex-pro/web-shared';

export type PipelineStageKey = 'COMPLETED' | 'IN_REVIEW' | 'TO_BE_INVOICED' | 'INVOICED';

export interface PipelineStage {
  readonly key: PipelineStageKey;
  readonly jobs: number;
  readonly cents: number | null;
  /** COMPLETED only: how many of `jobs` were sent back for re-capture. */
  readonly sentBack: number | null;
}

export interface CountCents {
  readonly count: number;
  readonly cents: number;
}

export interface OutstandingCustomer {
  readonly haulerOperatorId: string | null;
  readonly operatorId: string | null;
  readonly name: string;
  readonly invoices: number;
  readonly cents: number;
}

export interface InvoiceMetrics {
  readonly from: string;
  readonly to: string;
  readonly sent: number;
  readonly sentCents: number;
  readonly paid: number;
  readonly paidCents: number;
  readonly avgDaysToPay: number | null;
  readonly invoicedLoads: number;
  readonly invoicedBbl: string;
}

export interface JobTypeRevenue {
  readonly jobTypeId: number | null;
  readonly label: string;
  readonly jobs: number;
  readonly cents: number;
}

export interface VolumePoint {
  readonly day: string;
  readonly loads: number;
  readonly bbl: string;
}

export interface MoneyCount {
  readonly invoiceCount: number;
  readonly totalCents: number;
}

export interface FinanceDashboardResponse {
  readonly generatedAt: string;
  readonly companyTimezone: string;
  readonly viewerTimezone: string;
  readonly pipeline: readonly PipelineStage[];
  readonly aging: {
    readonly current: CountCents;
    readonly d31_60: CountCents;
    readonly d61_90: CountCents;
    readonly d90plus: CountCents;
    readonly total: CountCents;
  };
  readonly outstanding: readonly OutstandingCustomer[];
  readonly paymentTerms: {
    readonly overdue: MoneyCount;
    readonly dueNext7Days: MoneyCount;
    readonly withoutDueDate: MoneyCount;
    readonly paidInRange: {
      readonly invoiceCount: number;
      readonly paidOnTimeCount: number;
      readonly paidOnTimePct: number | null;
      readonly withoutDueDateCount: number;
    };
    readonly averageDaysLate: number | null;
    readonly lateCount: number;
  };
  readonly current: InvoiceMetrics;
  readonly previous: InvoiceMetrics;
  readonly changes: {
    readonly sentPct: number | null;
    readonly sentCentsPct: number | null;
    readonly paidPct: number | null;
    readonly paidCentsPct: number | null;
    readonly invoicedBblPct: number | null;
  };
  readonly revenueByJobType: readonly JobTypeRevenue[];
  readonly invoicedVolume: readonly VolumePoint[];
  readonly accountingExport: {
    readonly cadence: 'ON_DEMAND' | 'SCHEDULED';
    readonly lastExportAt: string | null;
    readonly lastExportInvoices: number | null;
    readonly notYetExported: number;
    readonly notYetExportedCents: number;
  };
}

export function useFinanceDashboard(
  apiOpts: ReturnType<typeof useApiOpts>,
  from: string,
  to: string,
): ReturnType<typeof useApiQuery<FinanceDashboardResponse>> {
  const qs = new URLSearchParams({ from, to }).toString();
  return useApiQuery<FinanceDashboardResponse>(apiOpts, `/v1/dashboards/finance?${qs}`, {
    queryKey: ['dashboards', 'finance', from, to] as const,
    refetchInterval: 5 * 60_000,
  });
}
